/**
 * ESC/POS receipt rendering using bizpluspos-print-core.
 */
import {
  OmnithermError,
  OmnithermErrorCode,
  print,
  ThermalDocument,
  scanBleDevices,
  getBondedBleDevices,
  discoverBleDevices,
  ensureNativeBleReady,
  scanUsbDevices,
  WebBluetoothTransport,
  isQzAvailable,
  isWebUsbAvailable,
  isWebBluetoothAvailable,
  isWebSerialAvailable,
  listQzPrinters,
  scanSerialPorts,
} from './thermalPrintCore';
import { printNativeBleKeepAlive } from './nativeBlePrinterSession';
import { loadReceiptLogo } from './receiptLogo';
import type {
  ConnectionConfig,
  BleDevice,
  UsbDevice,
  SerialPortDevice,
  DiscoveredBleDevice,
  DiscoverBleOptions,
} from './thermalPrintCore';
import { Platform } from 'react-native';
import { isElectronRuntime } from '../utils/isElectron';
import {
  capitalizeFirstLetter,
  formatMoney,
  formatNumberDecimal,
  replaceCurrencyString,
  roundMoney,
} from '../utils/libs';
import { formatDisplayDateTime } from '../utils/formatDisplayDateTime';
import { getOrderLineProductDisplayName } from '../utils/productDisplayName';
import { toPrinterText } from '../utils/printerText';
import type {
  DynamicTableParams,
  OrderProductItem,
  Payment_Information,
} from '../screens/checkout/components/Receipt';
import {
  AMOUNT_RECEIVED_LABEL,
  buildReceiptTotalsSummary,
  unpaidLineAmount,
  unpaidLineQuantity,
  unpaidLinesTotal,
} from './receiptTotals';

export {
  scanBleDevices,
  getBondedBleDevices,
  discoverBleDevices,
  ensureNativeBleReady,
  scanUsbDevices,
  scanSerialPorts,
  isQzAvailable,
  isWebUsbAvailable,
  isWebBluetoothAvailable,
  isWebSerialAvailable,
  listQzPrinters,
};
export type {
  BleDevice,
  UsbDevice,
  SerialPortDevice,
  DiscoveredBleDevice,
  DiscoverBleOptions,
};

export type ThermalPrinterTarget =
  | {
      mode: 'bluetooth';
      macAddress: string;
    }
  | {
      mode: 'tcp';
      host: string;
      port: number;
      timeoutMs?: number;
    }
  | {
      mode: 'usb';
      vendorId: string;
      productId: string;
    }
  | {
      mode: 'network';
      printerName: string;
    }
  | {
      mode: 'web-bluetooth';
      bleServiceUuid?: string;
      bleCharacteristicUuid?: string;
      bleMtu?: number;
      bleDeviceId?: string;
      bleDeviceName?: string;
      bleNamePrefix?: string;
    }
  | {
      mode: 'web-serial';
      serialBaudRate: number;
      serialDataBits?: 7 | 8;
      serialStopBits?: 1 | 2;
      serialParity?: 'none' | 'even' | 'odd';
      serialFlowControl?: 'none' | 'hardware';
      serialBufferSize?: number;
      serialUsbVendorId?: string;
      serialUsbProductId?: string;
    }
  | {
      mode: 'serial';
      path: string;
      baudRate: number;
    }
  | {
      mode: 'spooler';
      printerName: string;
    };

export type WebBluetoothThermalProfile = {
  id: string;
  label: string;
  bleServiceUuid: string;
  bleCharacteristicUuid: string;
};

export const WEB_BLUETOOTH_THERMAL_PROFILES: WebBluetoothThermalProfile[] = [
  {
    id: 'generic-18f0',
    label: 'Generic BLE thermal printer',
    bleServiceUuid: '000018f0-0000-1000-8000-00805f9b34fb',
    bleCharacteristicUuid: '00002af1-0000-1000-8000-00805f9b34fb',
  },
  {
    id: 'e7810a71',
    label: 'BLE UART thermal printer',
    bleServiceUuid: 'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
    bleCharacteristicUuid: 'bef8d6c9-9c21-4c9e-b632-bd58c1009f9f',
  },
  {
    id: 'issc-transparent-uart',
    label: 'ISSC Transparent UART',
    bleServiceUuid: '49535343-fe7d-4ae5-8fa9-9fafd205e455',
    bleCharacteristicUuid: '49535343-8841-43f4-a8d4-ecbe34729bb3',
  },
];

export const DEFAULT_WEB_BLUETOOTH_THERMAL_PROFILE =
  WEB_BLUETOOTH_THERMAL_PROFILES[0];

export async function pairWebBluetoothThermalPrinter(): Promise<{
  id?: string;
  name: string;
  profile: WebBluetoothThermalProfile;
}> {
  const bt = (navigator as any)?.bluetooth;
  if (!bt) throw new Error('Web Bluetooth is not available in this browser.');

  const allServiceUuids = WEB_BLUETOOTH_THERMAL_PROFILES.map(
    p => p.bleServiceUuid,
  );

  // Show ALL nearby BLE devices — no filtering at the picker
  const device: any = await bt.requestDevice({
    acceptAllDevices: true,
    optionalServices: allServiceUuids,
  });

  if (!device?.gatt) {
    throw new Error('Selected device does not support Bluetooth GATT.');
  }

  const server = await device.gatt.connect();

  // Probe each known thermal printer profile to see if the device exposes it
  let matchedProfile: WebBluetoothThermalProfile | null = null;
  for (const profile of WEB_BLUETOOTH_THERMAL_PROFILES) {
    try {
      const svc = await server.getPrimaryService(profile.bleServiceUuid);
      await svc.getCharacteristic(profile.bleCharacteristicUuid);
      matchedProfile = profile;
      break;
    } catch {
      // Service/characteristic not found — try next profile
    }
  }

  server.disconnect();

  if (!matchedProfile) {
    const label = device.name || 'Selected device';
    throw new Error(
      `"${label}" is not a supported thermal printer. No known printer service was found on this device.`,
    );
  }

  return {
    id: device.id,
    name: device.name || 'BLE Printer',
    profile: matchedProfile,
  };
}

function getErrorText(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err || '');
}

function getHostDesktopOs(): 'windows' | 'macos' | 'other' {
  if (typeof navigator === 'undefined') return 'other';
  const uaDataPlatform = (navigator as any)?.userAgentData?.platform;
  const platform = String(
    uaDataPlatform || navigator.platform || '',
  ).toLowerCase();
  const ua = String(navigator.userAgent || '').toLowerCase();
  const source = `${platform} ${ua}`;
  if (source.includes('win')) return 'windows';
  if (source.includes('mac')) return 'macos';
  return 'other';
}

function isDesktopUsbDriverError(err: unknown): boolean {
  const text = getErrorText(err);
  return (
    text.includes('LIBUSB_ERROR_NOT_SUPPORTED') ||
    text.includes('USB connection failed')
  );
}

function getDesktopUsbSetupMessage(): string {
  const hostOs = getHostDesktopOs();
  if (hostOs === 'windows') {
    return 'Add this printer in Windows Settings > Bluetooth & devices > Printers & scanners, then select it under OS Printer.';
  }
  if (hostOs === 'macos') {
    return 'Add this printer in System Settings > Printers & Scanners, then select it under OS Printer.';
  }
  return 'Add this printer in your system printer settings, then select it under OS Printer.';
}

export function getThermalPrintErrorMessage(err: unknown): string {
  const isWeb = Platform.OS === 'web';
  if (isDesktopUsbDriverError(err)) {
    return getDesktopUsbSetupMessage();
  }
  if (getErrorText(err).includes('Bluetooth print timed out')) {
    return 'Bluetooth print timed out. Make sure the printer is on and nearby, then try again.';
  }
  const errorText = getErrorText(err).toLowerCase();
  if (
    errorText.includes('bluetooth adapter is not enabled') ||
    errorText.includes('bluetooth is not enabled')
  ) {
    return 'Bluetooth is off. Turn on Bluetooth, then try printing again.';
  }
  if (errorText.includes('can not find the specified printing device')) {
    return "Printer not reachable. Make sure it is switched on, in range, and paired in your phone's Bluetooth settings.";
  }
  if (errorText.includes('bluetooth permission denied')) {
    return 'Bluetooth permission is required to print. Grant it and try again.';
  }
  if (!(err instanceof OmnithermError)) {
    return 'Printing failed. Check printer connection and settings.';
  }
  switch (err.code) {
    case OmnithermErrorCode.DEVICE_NOT_FOUND:
      return isWeb
        ? 'Printer not found. Make sure it is connected and try again from Printer Settings.'
        : 'Printer not found. Re-scan and select the correct printer.';
    case OmnithermErrorCode.CONNECTION_TIMEOUT:
      return 'Printer connection timed out. Check signal/network and try again.';
    case OmnithermErrorCode.CONNECTION_FAILED:
      return 'Could not connect to printer. Check power and connection details.';
    case OmnithermErrorCode.PERMISSION_DENIED:
      return isWeb
        ? 'Printer access was denied. Please allow access when prompted and try again.'
        : 'Printer permission denied. Grant permission and retry.';
    case OmnithermErrorCode.INVALID_CONFIG:
      return isWeb
        ? 'Printer settings are incomplete. Set up a printer in Printer Settings first.'
        : 'Printer settings are invalid. Review address/port or device IDs.';
    case OmnithermErrorCode.NO_TRANSPORT_AVAILABLE:
      return isWeb
        ? 'No printer connection available. Connect a USB printer or set up network printing in Settings.'
        : 'No compatible print transport is available on this device.';
    case OmnithermErrorCode.WRITE_FAILED:
      return 'Connected but failed to send print data to the printer.';
    default:
      return (
        err.message || 'Printing failed. Check printer connection and settings.'
      );
  }
}

const getOrderLineDisplayName = (item: OrderProductItem): string =>
  getOrderLineProductDisplayName(item);

function toConnectionConfig(target: ThermalPrinterTarget): ConnectionConfig {
  if (target.mode === 'bluetooth') {
    return { type: 'bluetooth', macAddress: target.macAddress };
  }
  if (target.mode === 'tcp') {
    return {
      type: 'tcp',
      host: target.host,
      port: target.port,
      timeout: target.timeoutMs,
    };
  }
  if (target.mode === 'network') {
    return { type: 'tcp', printerName: target.printerName };
  }
  if (target.mode === 'web-bluetooth') {
    const profile = DEFAULT_WEB_BLUETOOTH_THERMAL_PROFILE;
    return {
      type: 'bluetooth',
      bleServiceUuid: target.bleServiceUuid || profile.bleServiceUuid,
      bleCharacteristicUuid:
        target.bleCharacteristicUuid || profile.bleCharacteristicUuid,
      bleNamePrefix: target.bleNamePrefix,
    };
  }
  if (target.mode === 'web-serial') {
    const serialUsbVendorId = parseOptionalUsbId16(target.serialUsbVendorId);
    const serialUsbProductId = parseOptionalUsbId16(target.serialUsbProductId);
    return {
      type: 'serial',
      serialBaudRate: target.serialBaudRate,
      serialDataBits: target.serialDataBits,
      serialStopBits: target.serialStopBits,
      serialParity: target.serialParity,
      serialFlowControl: target.serialFlowControl,
      serialBufferSize: target.serialBufferSize,
      serialUsbVendorId,
      serialUsbProductId,
    };
  }
  if (target.mode === 'serial') {
    return {
      type: 'serial',
      path: target.path,
      baudRate: target.baudRate,
    };
  }
  if (target.mode === 'spooler') {
    return {
      type: 'spooler',
      printerName: target.printerName,
    };
  }

  const vendorId = parseUsbId16(target.vendorId);
  const productId = parseUsbId16(target.productId);
  if (Number.isNaN(vendorId) || Number.isNaN(productId)) {
    throw new Error('Invalid USB vendor or product ID');
  }
  return { type: 'usb', vendorId, productId };
}

function parseUsbId16(raw: string): number {
  const t = raw.trim();
  if (!t) return NaN;
  const n = /^0x/i.test(t)
    ? parseInt(t.replace(/^0x/i, ''), 16)
    : parseInt(t, 10);
  if (!Number.isFinite(n) || n < 0 || n > 0xffff) return NaN;
  return n;
}

function parseOptionalUsbId16(raw: string | undefined): number | undefined {
  const value = String(raw || '').trim();
  if (!value) return undefined;
  const parsed = parseUsbId16(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

function getDocWidth(widthMM?: number): 58 | 80 {
  return (widthMM ?? 58) >= 72 ? 80 : 58;
}

/** A logo not ready by then is left off; the receipt prints without it. */
const LOGO_WAIT_MS = 3000;

/**
 * Prints the order receipt in the usual till-slip layout: shop header,
 * receipt details, items, one TOTAL with how it was paid, then the footer.
 */
export async function printOrderThermalReceipt(
  {
    receipt,
    business,
    employeedata,
    currency,
    totalsMode,
    logoUrl,
  }: DynamicTableParams,
  {
    charsPerLine,
    autoCut,
    widthMM,
  }: { charsPerLine: number; autoCut: boolean; widthMM?: number },
  target: ThermalPrinterTarget,
): Promise<void> {
  const chars = Math.max(24, Math.min(48, charsPerLine || 32));
  const doc = new ThermalDocument({
    width: getDocWidth(widthMM),
    charsPerLine: chars,
  });
  const logo = logoUrl
    ? await withTimeout(
        loadReceiptLogo(logoUrl, getDocWidth(widthMM) === 80 ? 576 : 384),
        LOGO_WAIT_MS,
        'Logo timed out',
      ).catch(() => null)
    : null;

  // Every text below goes through toPrinterText before it is measured, so
  // emoji and accents neither print as garbage nor shift the columns.
  const currencyText = toPrinterText(replaceCurrencyString('Ksh', currency));
  const date = toPrinterText(formatDisplayDateTime(new Date()));

  const name = toPrinterText(business?.name || '');
  const desc = (business?.description || '').trim();
  const phone = toPrinterText(
    String(employeedata?.bp_owner_phone_number || ''),
  );
  const employeeName = toPrinterText(
    String(employeedata?.bp_employee_name || ''),
  );
  const receiptSource = receipt as typeof receipt & {
    receiptID?: string;
    bp_order_master_receipt_reference_id?: string;
  };
  const receiptId = toPrinterText(
    String(
      receiptSource?.ReceiptID ||
        receiptSource?.receiptID ||
        receiptSource?.bp_order_master_receipt_reference_id ||
        '',
    ),
  );

  // Logo on top, as part of the document: every transport now sends the
  // bytes as encoded, so the raster prints wherever the text does.
  if (logo) {
    doc.image(logo.base64, { width: logo.widthDots });
  }

  // Lines are centered and right-aligned with spaces: the encoder resets the
  // alignment before the line feed, and cheap printers align at the feed.
  if (name && name.length * 2 <= chars) {
    doc
      .text(' '.repeat(Math.floor((chars - name.length * 2) / 2)))
      .line(name, { bold: true, width: 2, height: 2 });
  } else {
    for (const l of centerWrapped(name, chars)) {
      doc.line(l, { bold: true, height: 2 });
    }
  }
  for (const seg of desc.split(/\r?\n/)) {
    for (const l of centerWrapped(toPrinterText(seg), chars)) doc.line(l);
  }
  if (phone) {
    for (const l of centerWrapped(`Tel ${phone}`, chars)) doc.line(l);
  }

  // Receipt details; a field with no value is left out.
  doc.separator();
  const details: Array<[string, string]> = [
    ['Receipt No.', receiptId],
    ['Date', date],
    ['Served by', employeeName],
  ];
  for (const [label, value] of details) {
    if (!value) continue;
    for (const l of leftRight(label, value, chars)) doc.line(l);
  }
  doc.separator();

  // Order items
  const order = receipt?.order || [];
  const items = order
    .filter(item => typeof item === 'object' && item.bp_product_information)
    .map(item => {
      const info = item.bp_product_information;
      const unit =
        info.bp_product_price_type === 'unit'
          ? toPrinterText(info.bp_product_unit_of_mass || '')
          : '';
      const qty = formatNumberDecimal(unpaidLineQuantity(item));
      const price = info.bp_product_selling_price;
      return {
        // A name made only of emoji still gets a label.
        name: toPrinterText(getOrderLineDisplayName(item)) || 'Item',
        qty: unit ? `${qty} ${unit}` : qty,
        price: String(roundMoney(price)),
        amount: formatMoney(unpaidLineAmount(item)),
      };
    });

  // Item numbers are right-aligned so the names line up: " 9. ", "10. ".
  // Wrapped names and the qty line start under the name, not the number.
  const numberW = String(items.length).length + 2;
  const indent = ' '.repeat(numberW);
  const itemNumber = (i: number) => `${i + 1}.`.padStart(numberW - 1) + ' ';
  const header = `${'#'.padEnd(numberW)}Item`;

  // Wide paper: # / Item / Qty / Price / Amount on one row, each value column
  // a space wider than its widest value. Narrow paper, or when that leaves the
  // name under 16 columns: the name on its own line, "qty x price" and the
  // amount on the line below.
  const colW = (key: 'qty' | 'price' | 'amount', min: number) =>
    Math.max(min, ...items.map(item => item[key].length + 1));
  const valueW = [colW('qty', 7), colW('price', 10), colW('amount', 10)];
  const nameW = chars - numberW - valueW[0] - valueW[1] - valueW[2];
  if (nameW >= 16) {
    const widths = [numberW + nameW, ...valueW];
    doc.line(row([header, 'Qty', 'Price', 'Amount'], widths), { bold: true });
    doc.separator();
    items.forEach((item, i) => {
      const [first, ...more] = wrapItemLabelToItemColumnWidth(item.name, nameW);
      const cells = [itemNumber(i) + first, item.qty, item.price, item.amount];
      doc.line(row(cells, widths));
      for (const l of more) doc.line(indent + l);
    });
  } else {
    doc.line(row([header, 'Amount'], [chars - 6, 6]), { bold: true });
    items.forEach((item, i) => {
      const nameLines = wrapItemLabelToItemColumnWidth(
        item.name,
        chars - numberW,
      );
      nameLines.forEach((l, li) => {
        doc.line((li === 0 ? itemNumber(i) : indent) + l);
      });
      const qtyLine = `${indent}${item.qty} x ${item.price}`;
      for (const l of leftRight(qtyLine, item.amount, chars)) doc.line(l);
    });
  }
  doc.separator();

  // Totals: one TOTAL, then payment, change and balance under it.
  const pi: Payment_Information = receipt.Payment_Information;
  const totalAmount = unpaidLinesTotal(order);
  const summary = buildReceiptTotalsSummary(pi, totalAmount, totalsMode ?? 'print');

  // "TOTAL" when the figure is the whole order. On an installment or
  // partial-items slip an Order Total row shows the whole order, and this
  // figure keeps its "Sub-Total" label.
  const isWholeOrder = !summary.rows.some(
    r => r.label === 'Order Total' && r.amount !== summary.grandTotal,
  );
  const totalLabel = isWholeOrder ? 'TOTAL' : 'Sub-Total';
  const totalValue = `${currencyText} ${formatMoney(summary.grandTotal)}`;
  for (const l of leftRight(totalLabel, totalValue, chars)) {
    doc.line(l, { bold: true, height: 2 });
  }

  // "Paid by <method>": Amount Received is this payment, the latest one, as in
  // PaymentModal; Total Paid covers every payment, so it only names a method
  // they all share.
  const methods = (pi?.bp_payments ?? []).map(p =>
    toPrinterText(String(p?.method ?? '')),
  );
  const lastMethod = methods[methods.length - 1] ?? '';
  const sharedMethod = methods.every(
    m => m.toLowerCase() === lastMethod.toLowerCase(),
  )
    ? lastMethod
    : '';
  for (const r of summary.rows) {
    // The big line above already shows this figure.
    if (
      (r.label === 'Order Total' || r.label === 'Sub-Total') &&
      r.amount === summary.grandTotal
    ) {
      continue;
    }
    const method =
      r.label === AMOUNT_RECEIVED_LABEL
        ? lastMethod
        : r.label === 'Total Paid'
          ? sharedMethod
          : '';
    const label = method ? `Paid by ${capitalizeFirstLetter(method)}` : r.label;
    const valueStr = formatMoney(r.amount);
    // Only change and balance stand out next to the TOTAL line.
    for (const l of leftRight(label, valueStr, chars)) {
      doc.line(l, { bold: !!r.emphasis });
    }
  }

  if (summary.isPartialLedger && summary.isPaidInFull) {
    doc.line(centerLine('*** PAID IN FULL ***', chars), { bold: true });
  }
  doc.separator();

  // Footer
  const policy = 'GOODS ONCE SOLD WILL NOT BE ACCEPTED';
  for (const l of centerWrapped(policy, chars)) doc.line(l);
  for (const l of centerWrapped(`Thank you for choosing ${name}!`, chars)) {
    doc.line(l, { bold: true });
  }
  // Font B is 9 dots wide to Font A's 12, so a line holds 4/3 as many.
  const smallChars = Math.floor((chars * 4) / 3);
  const poweredBy = 'Powered by BizPlus POS - +254759266327';
  doc.feed(1);
  for (const l of centerWrapped(poweredBy, smallChars)) {
    doc.line(l, { font: 'B' });
  }
  doc.cut('full', { feed: Platform.OS === 'web' ? 3 : 0 });
  // if (autoCut) {
  //   doc.cut('full', { feed: getCutFeedLines() });
  // }

  await executePrint(doc, target);
}

async function executePrint(
  doc: ThermalDocument,
  target: ThermalPrinterTarget,
): Promise<void> {
  if (isElectronRuntime()) {
    if (target.mode === 'web-bluetooth') {
      await setElectronBluetoothPrintTarget(target);
    }
    const { print: electronPrint } = await import('./electronPrintService');
    try {
      const printPromise = electronPrint(doc, toConnectionConfig(target));
      await (target.mode === 'web-bluetooth'
        ? withTimeout(printPromise, 7000, 'Bluetooth print timed out')
        : printPromise);
    } finally {
      if (target.mode === 'web-bluetooth') {
        await clearElectronBluetoothPrintTarget();
      }
    }
    return;
  }
  if (target.mode === 'bluetooth') {
    await printNativeBleKeepAlive(doc, target.macAddress);
    return;
  }
  await print(doc, toConnectionConfig(target));
}

function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string,
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(label)), ms),
    ),
  ]);
}

async function setElectronBluetoothPrintTarget(
  target: Extract<ThermalPrinterTarget, { mode: 'web-bluetooth' }>,
): Promise<void> {
  const ipcRenderer = (window as any)?.electron?.ipcRenderer;
  if (!ipcRenderer?.invoke) return;
  await ipcRenderer.invoke('bluetooth:set-print-target', {
    deviceId: target.bleDeviceId,
    deviceName: target.bleDeviceName,
    namePrefix: target.bleNamePrefix || target.bleDeviceName,
  });
}

async function clearElectronBluetoothPrintTarget(): Promise<void> {
  const ipcRenderer = (window as any)?.electron?.ipcRenderer;
  if (!ipcRenderer?.invoke) return;
  await ipcRenderer.invoke('bluetooth:clear-print-target');
}

/**
 * Word-wrap text to `maxWidth` columns, breaking between words; only a word
 * longer than the width is split.
 */
function wrapItemLabelToItemColumnWidth(
  text: string,
  maxWidth: number,
): string[] {
  const t = text.trim();
  if (!t) {
    return [''];
  }
  const w = Math.max(1, maxWidth);
  const words = t.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= w) {
      current = candidate;
      continue;
    }
    if (current) {
      lines.push(current);
      current = '';
    }
    if (word.length <= w) {
      current = word;
    } else {
      let rest = word;
      while (rest.length > w) {
        lines.push(rest.slice(0, w));
        rest = rest.slice(w);
      }
      current = rest;
    }
  }
  if (current) {
    lines.push(current);
  }
  return lines.length ? lines : [t.slice(0, w)];
}

/**
 * Fixed-width row: the first column left-aligned, the others right-aligned.
 * `widths` should add up to `chars`.
 */
function row(cols: string[], widths: number[]): string {
  return cols
    .map((col, i) => {
      const cell = col.slice(0, widths[i]);
      return i === 0
        ? cell.padEnd(widths[i], ' ')
        : cell.padStart(widths[i], ' ');
    })
    .join('');
}

/**
 * `left` flush left and `right` flush right on one line. When they don't fit
 * together, the value moves to its own right-aligned line(s) so the printer
 * never breaks a line mid-word.
 */
function leftRight(left: string, right: string, width: number): string[] {
  if (left.length + 1 + right.length <= width) {
    return [left + right.padStart(width - left.length, ' ')];
  }
  // Keep the left side's indent on every line it wraps to.
  const indent = ' '.repeat(left.length - left.trimStart().length);
  return [
    ...wrapItemLabelToItemColumnWidth(left, width - indent.length).map(
      l => indent + l,
    ),
    ...wrapItemLabelToItemColumnWidth(right, width).map(l =>
      l.padStart(width, ' '),
    ),
  ];
}

function centerLine(text: string, width: number): string {
  const value = String(text || '').trim();
  if (!value) return '';
  const line = value.length > width ? value.slice(0, width) : value;
  const leftPad = Math.max(0, Math.floor((width - line.length) / 2));
  return `${' '.repeat(leftPad)}${line}`;
}

/** Word-wrap `text` to `width` columns and center each line with spaces. */
function centerWrapped(text: string, width: number): string[] {
  const value = text.trim();
  if (!value) return [];
  return wrapItemLabelToItemColumnWidth(value, width).map(l =>
    centerLine(l, width),
  );
}

/**
 * Sample receipt for printer settings test buttons.
 */
export async function printSampleThermalReceipt(
  charsPerLine: number,
  opts: { autoCut?: boolean; beep?: boolean },
  target: ThermalPrinterTarget,
): Promise<void> {
  const chars = Math.max(24, Math.min(48, charsPerLine || 32));
  const doc = new ThermalDocument({ width: 58, charsPerLine: chars });
  // Middle column must fit "Sub-Total" (9 chars); was 8 → truncated to "Sub-Tota".
  const priceW = 10;
  // Amounts carry up to two decimals; "150,000.55" needs 10 columns.
  const amtW = 10;
  const itemW = Math.max(8, chars - priceW - amtW);
  const widths: [number, number, number] = [itemW, priceW, amtW];

  const testDate = toPrinterText(new Date().toLocaleDateString());
  const testTime = toPrinterText(new Date().toLocaleTimeString());

  doc
    .line('BizPlus POS', { align: 'center', bold: true, height: 2, width: 2 })
    .line('PRINTER TEST', { align: 'center', underline: true })
    .feed(1)
    .line(row(['# Item', 'Price', 'Amount'], widths), { bold: true })
    .line(row(['1.Coca Cola 500ml', '2x50', '100'], widths))
    .line(row(['2.Bread White 400g', '1x85', '85'], widths))
    .line(row(['3.Milk Fresh 1L', '1x120', '120'], widths))
    .feed(1)
    .line(row(['', 'Sub-Total', '305'], widths))
    .line(row(['', 'Discount', '0'], widths))
    .line(row(['', 'Total', 'Ksh305'], widths))
    .line('.'.repeat(28), { align: 'center' })
    .line(`Test Date: ${testDate}`)
    .line(`Test Time: ${testTime}`)
    .feed(1)
    .line('If you can read this clearly,', { align: 'center' })
    .line('your printer is working', { align: 'center' })
    .feed(1)
    .line('BizPlus POS - +254759266327', { align: 'center' });

  if (opts.beep) {
    doc.beep(1);
  }

  doc.cut('full', { feed: Platform.OS === 'web' ? 3 : 0 });

  await executePrint(doc, target);
}
